// ============================================================
// tara.js
// EV Powertrain Simulator — core 모듈
// 사이버보안 — TARA Part4, CAL 매트릭스, ISO 26262-6, 안전 삼각형
// Extracted from ev-sim_v1_0_Verification_46.jsx (Phase 2)
// ============================================================

import { TARA_SYSTEM_ASSETS, TARA_DAMAGE_SCENARIOS, TARA_THREATS }
  from '../../constants/TARA_DATA.js';

// ── computeAttackFeasibility (ISO/SAE 21434 §15.4) ──
// [Fix-4] '고'/'중'/'저' 단순 문자열 대신 5개 인자 정량 합산으로 공격 실현 가능성 산출
// factors: { elapsed_time, expertise, knowledge, equipment, window }
export function computeAttackFeasibility(factors = {}) {
  const ELAPSED_MAP   = { '<1d':10, '<1w':9, '<1m':8, '<3m':6, '<6m':4, '<1y':2, '>1y':0 };
  const EXPERTISE_MAP = { layman:0, proficient:3, expert:6, multiple:9 };
  const KNOWLEDGE_MAP = { public:0, restricted:3, confidential:7, 'top-secret':10 };
  const EQUIP_MAP     = { standard:0, specialized:4, bespoke:7, multiple:9 };
  const WINDOW_MAP    = { unlimited:0, easy:1, moderate:4, difficult:10 };

  const elapsed   = ELAPSED_MAP  [factors.elapsed_time] ?? 5;
  const expertise = EXPERTISE_MAP[factors.expertise]    ?? 3;
  const knowledge = KNOWLEDGE_MAP[factors.knowledge]    ?? 3;
  const equipment = EQUIP_MAP    [factors.equipment]    ?? 4;
  const window    = WINDOW_MAP   [factors.window]       ?? 4;

  const total = elapsed + expertise + knowledge + equipment + window;
  // ISO/SAE 21434 Table 12: 합계 → 등급
  // [P-14-Fix] Medium 상한을 19→13, Low 상한을 24→19, Very Low ≥20 으로 표준 준수
  //   High:     0–9  (total ≤  9)
  //   Medium:  10–13 (total ≤ 13)  ← 이전: ≤19 → CAL 과분류 오류
  //   Low:     14–19 (total ≤ 19)
  //   Very Low: ≥20
  const rating   = total <= 9  ? 'High'   :
                   total <= 13 ? 'Medium' :
                   total <= 19 ? 'Low'    : 'Very Low';
  const ratingKo = { High:'고', Medium:'중', Low:'저', 'Very Low':'저' }[rating];
  return { total, elapsed, expertise, knowledge, equipment, window, rating, ratingKo };
}

// ── computeTARA_Part4 ──
/**
 * ISO/SAE 21434 Part 4 기반 사이버보안 위험 분석(TARA)을 수행한다.
 *
 * Step 1: 자산별 안전 관련성 집계 (TARA_SYSTEM_ASSETS × TARA_DAMAGE_SCENARIOS × TARA_THREATS)
 * Step 2: 손상 시나리오 리스크 계산
 *   risk_value = impact × likelihood / 6 (6점 척도 정규화)
 *   [A68-07-Fix-CS1] 구 /10 공식 → /6으로 수정: CRITICAL 달성 가능하도록 스케일 조정
 *   CRITICAL: ≥6, HIGH: ≥4, MEDIUM: ≥2, LOW: <2
 * Step 3: 위협별 공격 경로 분석 및 대응 방안 도출
 * Step 4: ASIL 요구사항 도출 ([B-7 Fix] severity→S, likelihood→E, impact_safety→C 변환 후 Table B.1 조회)
 * Step 5: 전체 리스크 매트릭스 통계 및 ISO 26262 Part 4 인증 준비 여부 판정
 *
 * @param {object}   params       차량 파라미터 (현재 미사용, 확장 예약)
 * @param {object[]|null} haraResults  analyzeHARA() 결과 (HARA 연계용, null 허용)
 * @returns {{ asset_analysis, damage_analysis, threat_analysis, stats }}
 */
function computeTARA_Part4(params, haraResults) {
  // Step 1: 자산별 안전 관련성 집계
  const asset_analysis = TARA_SYSTEM_ASSETS.map(asset => {
    const related_ds = TARA_DAMAGE_SCENARIOS.filter(d => d.asset_id === asset.id);
    const related_threats = TARA_THREATS.filter(t => t.asset_ids.includes(asset.id));
    const max_severity = related_ds.length > 0 ? Math.max(...related_ds.map(d => d.severity)) : 0;
    return { ...asset, damage_scenario_count: related_ds.length,
             threat_count: related_threats.length, max_severity };
  });

  // Step 2: 손상 시나리오 상세 분석
  const damage_analysis = TARA_DAMAGE_SCENARIOS.map(ds => {
    const asset = TARA_SYSTEM_ASSETS.find(a => a.id === ds.asset_id);
    const threats = TARA_THREATS.filter(t => t.asset_ids.includes(ds.asset_id));

    // [A68-07-Fix-CS1] 리스크 공식 수정: /10 → /6 (6점 likelihood 척도 정규화)
    // 이전: impact * likelihood / 10 → max=6.0 < CRITICAL임계 7 → CRITICAL 불가
    // 수정: /6 → max=10, CRITICAL>=6, HIGH>=4, MEDIUM>=2
    const impact = ds.severity;
    const risk_value = +(impact * ds.likelihood / 6).toFixed(1);
    const risk_level = risk_value >= 6 ? "CRITICAL" : risk_value >= 4 ? "HIGH" : risk_value >= 2 ? "MEDIUM" : "LOW";

    // 리스크 처리 결정
    const treatment = risk_level === "CRITICAL" ? "즉시 완화 (Mitigation)" :
                      risk_level === "HIGH"      ? "리스크 수용 조건부" :
                      risk_level === "MEDIUM"    ? "모니터링" : "수용 (Accept)";
    const treatmentEn = risk_level === "CRITICAL" ? "Immediate Mitigation" :
                        risk_level === "HIGH"      ? "Conditional Acceptance" :
                        risk_level === "MEDIUM"    ? "Monitoring" : "Accept";

    // ASIL 요구사항 — [B-7 Fix] ISO 26262-3 Table B.1 S×E×C 3요소 판정
    // 구: severity 단독 임계값 → HARA S+E+C와 불일치
    // 수정: severity(1-10)→S(0-3), likelihood(1-6)→E(1-4), impact_safety 존재 여부→C(2|3)
    const _B7_S = ds.severity >= 9 ? 3 : ds.severity >= 7 ? 2 : ds.severity >= 4 ? 1 : 0;
    const _B7_E = ds.likelihood >= 5 ? 4 : ds.likelihood >= 3 ? 3 : ds.likelihood >= 2 ? 2 : 1;
    const _B7_C = ds.impact_safety ? 3 : 2; // 안전 영향 있으면 C=3(제어불능), 없으면 C=2
    const _B7_TABLE = {
      '3-4-3':'D','3-4-2':'D','3-4-1':'C','3-3-3':'D','3-3-2':'C','3-3-1':'B',
      '3-2-3':'C','3-2-2':'B','3-2-1':'A','3-1-3':'B','3-1-2':'A','3-1-1':'QM',
      '2-4-3':'C','2-4-2':'B','2-4-1':'A','2-3-3':'B','2-3-2':'A','2-3-1':'QM',
      '2-2-3':'A','2-2-2':'QM','2-2-1':'QM','2-1-3':'QM','2-1-2':'QM','2-1-1':'QM',
      '1-4-3':'A','1-4-2':'QM','1-4-1':'QM','1-3-3':'QM','1-3-2':'QM','1-3-1':'QM',
      '1-2-3':'QM','1-2-2':'QM','1-2-1':'QM','1-1-3':'QM','1-1-2':'QM','1-1-1':'QM',
      '0-4-3':'QM','0-4-2':'QM','0-4-1':'QM','0-3-3':'QM','0-3-2':'QM','0-3-1':'QM',
      '0-2-3':'QM','0-2-2':'QM','0-2-1':'QM','0-1-3':'QM','0-1-2':'QM','0-1-1':'QM',
    };
    const _B7_raw = _B7_TABLE[`${_B7_S}-${_B7_E}-${_B7_C}`] ?? 'QM';
    const required_asil = _B7_raw === 'QM' ? 'QM' : `ASIL ${_B7_raw}`;

    // HARA 연계
    const hara_link = haraResults ? haraResults.find(h => h.id === ds.fm_link) : null;

    return {
      ...ds,
      asset_name: asset?.name || ds.asset_id,
      asset_nameEn: asset?.nameEn || asset?.name || ds.asset_id,
      asset_safety: asset?.safety_relevance || "QM",
      threat_count: threats.length,
      impact, risk_value, risk_level, treatment, treatmentEn, required_asil,
      hara_asil: hara_link?.asil || null,
      security_controls: deriveSecurityControls(ds, threats),
    };
  });

  // Step 3: 위협별 공격 경로 분석
  const threat_analysis = TARA_THREATS.map(threat => {
    const related_ds = TARA_DAMAGE_SCENARIOS.filter(ds =>
      threat.asset_ids.includes(ds.asset_id)
    );
    const max_impact = related_ds.length > 0 ? Math.max(...related_ds.map(d => d.severity)) : 0;
    // [A68-07-Fix-CS1] threat_risk도 /6 공식 통일
    const threat_risk = +(max_impact * threat.likelihood / 6).toFixed(1);
    const attack_path = `${threat.attack_vector} → ${threat.asset_ids.slice(0,2).join(" / ")}`;

    return {
      ...threat,
      related_scenario_count: related_ds.length,
      max_impact,
      threat_risk,
      threat_risk_level: threat_risk >= 6 ? "CRITICAL" : threat_risk >= 4 ? "HIGH" : threat_risk >= 2 ? "MEDIUM" : "LOW",
      attack_path,
      countermeasure: deriveCountermeasure(threat),
    };
  });

  // Step 5: 전체 리스크 매트릭스 통계
  const critical_count = damage_analysis.filter(d => d.risk_level === "CRITICAL").length;
  const high_count     = damage_analysis.filter(d => d.risk_level === "HIGH").length;
  const asil_d_required = damage_analysis.filter(d => d.required_asil === "ASIL D").length;

  // TARA 완료 판정
  const tara_complete = critical_count === 0;
  const certification_readiness = critical_count === 0 && high_count <= 2
    ? "ISO 26262 Part 4 인증 준비 완료"
    : critical_count > 0
    ? "Critical 항목 즉시 처리 필요"
    : "조건부 인증 가능 (High 항목 검토 후)";
  const certification_readiness_en = critical_count === 0 && high_count <= 2
    ? "ISO 26262 Part 4 Certification Ready"
    : critical_count > 0
    ? "Critical items require immediate action"
    : "Conditional certification (review High items)";

  return {
    asset_analysis,
    damage_analysis,
    threat_analysis,
    stats: {
      total_assets:     TARA_SYSTEM_ASSETS.length,
      total_scenarios:  TARA_DAMAGE_SCENARIOS.length,
      total_threats:    TARA_THREATS.length,
      critical_count,
      high_count,
      medium_count:     damage_analysis.filter(d=>d.risk_level==="MEDIUM").length,
      low_count:        damage_analysis.filter(d=>d.risk_level==="LOW").length,
      asil_d_required,
      tara_complete,
      certification_readiness,
      certification_readiness_en,
    },
  };
}

// ── deriveSecurityControls ──
/**
 * 손상 시나리오와 연계 위협에 기반하여 적용 보안 통제 목록을 도출한다.
 *
 * 규칙 기반 도출:
 *   severity ≥ 9          → HW 안전 회로 독립 채널
 *   위조(Spoofing) 위협  → 메시지 인증 코드 (MAC/CMAC-AES128)
 *   DoS 위협             → 레이트 리미팅 + 워치독
 *   SW 변조 위협         → 코드 서명 + 보안 부트(TrustZone)
 *   해당 없음            → 기본 접근 제어
 *
 * @param {{ severity: number, impact_safety?: boolean }} ds  손상 시나리오
 * @param {Array<{ threat: string }>} threats  연계 위협 목록
 * @returns {{ ko: string[], en: string[] }}  한국어/영어 보안 통제 목록
 */
function deriveSecurityControls(ds, threats) {
  const controls = [];
  const controlsEn = [];
  if (ds.severity >= 9) { controls.push("HW 안전 회로 독립 채널"); controlsEn.push("Independent HW Safety Circuit"); }
  if (threats.some(t => t.threat.includes("위조") || t.threat.includes("Spoofing")))
    { controls.push("메시지 인증 코드 (MAC)"); controlsEn.push("Message Authentication Code (MAC)"); }
  if (threats.some(t => t.threat.includes("DoS")))
    { controls.push("레이트 리미팅 + 워치독"); controlsEn.push("Rate Limiting + Watchdog"); }
  if (threats.some(t => t.threat.includes("SW")))
    { controls.push("코드 서명 + 보안 부트"); controlsEn.push("Code Signing + Secure Boot"); }
  if (controls.length === 0) { controls.push("기본 접근 제어"); controlsEn.push("Basic Access Control"); }
  return { ko: controls, en: controlsEn };
}

// ── deriveCountermeasure ──
/**
 * 위협 유형에 따른 권고 대응 방안(Countermeasure)을 반환한다.
 *
 * 사전 정의된 맵에서 위협명을 조회하며, 미등록 위협은 '보안 정책 수립 필요'를 반환한다.
 * 주요 위협 유형별 대응:
 *   인증 우회      → 다중 인증(MFA) + 짧은 세션 토큰
 *   메시지 위조    → CAN FD MAC (CMAC-AES128)
 *   서비스 거부    → 이더넷 QoS + CAN 트래픽 필터링
 *   SW 무단 변조   → 코드 서명 + 안전 부트(TrustZone)
 *   데이터 도청    → TLS 1.3 + AES-256 암호화
 *   리플레이 공격  → 타임스탬프 + 롤링 카운터
 *
 * @param {{ threat: string }} threat  위협 정의 객체 (threat 필드: 위협명)
 * @returns {{ ko: string, en: string }}  한국어/영어 대응 방안
 */
function deriveCountermeasure(threat) {
  const map = {
    "인증 우회":         "다중 인증(MFA) + 세션 토큰 짧게",
    "메시지 위조 (Spoofing)": "CAN CANFD MAC (CMAC-AES128)",
    "서비스 거부 (DoS)": "이더넷 QoS + CAN 트래픽 필터링",
    "SW 무단 변조":      "코드 서명 검증 + 안전 부트(TrustZone)",
    "데이터 도청":       "TLS 1.3 + AES-256 암호화",
    "물리적 접근 공격":  "잠금 장치 + ECU 봉인 씰",
    "리플레이 공격":     "타임스탬프 + 롤링 카운터",
    "공급망 조작":       "하드웨어 UID + 공급업체 감사",
  };
  const mapEn = {
    "인증 우회":         "MFA + Short Session Tokens",
    "메시지 위조 (Spoofing)": "CAN FD MAC (CMAC-AES128)",
    "서비스 거부 (DoS)": "Ethernet QoS + CAN Traffic Filter",
    "SW 무단 변조":      "Code Signature Verify + Secure Boot (TrustZone)",
    "데이터 도청":       "TLS 1.3 + AES-256 Encryption",
    "물리적 접근 공격":  "Lock Mechanism + ECU Tamper Seal",
    "리플레이 공격":     "Timestamp + Rolling Counter",
    "공급망 조작":       "Hardware UID + Supplier Audit",
  };
  return { ko: map[threat.threat] || "보안 정책 수립 필요", en: mapEn[threat.threat] || "Security policy establishment required" };
}

// ── generateTARA_Report ──
/**
 * TARA 분석 결과를 CSV 보고서 문자열로 생성한다.
 *
 * 기본 컬럼: 시나리오ID, 자산명, 손상 시나리오, 안전 영향, 심각도, 발생가능성,
 *            리스크값, 리스크수준, ASIL요구, 처리방안
 *
 * pentestResults가 제공되면 다음 컬럼을 추가한다:
 *   침투테스트_결과, 발견사항/CVE, CVSS점수, 테스터, 메모
 *   [D10-Fix] ISO/SAE 21434 Cl.10 호환 침투 테스트 연계 기능
 *
 * @param {{ damage_analysis: object[] }} taraResult  computeTARA_Part4()의 반환값
 * @param {object|null} [pentestResults]  { [scenario_id]: { result, finding, cvss, tester, notes } }
 * @returns {string}  쉼표 구분 CSV 문자열
 */
// [D10-Fix] pentestResults 옵션 파라미터 추가 → CAL 위협 행에 침투 테스트 결과 병합
// pentestResults: { [threat_id]: { result, finding, cvss, tester, notes } }
function generateTARA_Report(taraResult, pentestResults = null) {
  const rows = ["시나리오ID,자산명,손상 시나리오,영향(안전),심각도,발생가능성,리스크값,리스크수준,ASIL요구,처리방안"];
  if (pentestResults) {
    rows[0] += ",침투테스트_결과,발견사항/CVE,CVSS점수,테스터,메모";
  }
  taraResult.damage_analysis.forEach(d => {
    const base = [d.id, `"${d.asset_name}"`, `"${d.scenario}"`, `"${d.impact_safety}"`,
                  d.severity, d.likelihood, d.risk_value, d.risk_level, d.required_asil, `"${d.treatment}"`];
    if (pentestResults) {
      const pt = pentestResults[d.id] || {};
      base.push(
        pt.result  || 'not_tested',
        `"${pt.finding || ''}"`,
        pt.cvss    || '',
        `"${pt.tester  || ''}"`,
        `"${pt.notes   || ''}"`,
      );
    }
    rows.push(base.join(","));
  });
  return rows.join("\n");
}

// ── generatePentestReport ──
/**
 * CAL 매트릭스와 침투 테스트 결과를 ISO/SAE 21434 Cl.10 형식 CSV 보고서로 생성한다.
 *
 * CAL 2 이상인 위협만 대상으로 하며, 각 위협의 CAL 등급·공격 벡터·보안 통제 상태와
 * 침투 테스트 결과(pass/fail/partial/not_tested)를 병합하여 단일 CSV로 출력한다.
 * CVSS 점수: ≥9 Critical, ≥7 High, ≥4 Medium, <4 Low.
 *
 * @param {{ cal_items: object[] }} calMatrix  computeCAL_Matrix()의 반환값
 * @param {object} [pentestResults]  { [threat_id]: { result, finding, cvss, tester, notes } }
 * @param {'ko'|'en'} [lang='ko']   출력 언어 (컬럼명 및 결과 레이블)
 * @returns {string}  CSV 문자열. calMatrix가 없으면 빈 문자열.
 */
// [D10] CAL 매트릭스 + 침투 테스트 결과 → ISO/SAE 21434 Cl.10 형식 CSV
function generatePentestReport(calMatrix, pentestResults = {}, lang = 'ko') {
  if (!calMatrix || !calMatrix.cal_items) return '';

  const ko = lang !== 'en';
  const RESULT_LABEL = {
    not_tested:  ko ? '미실시'   : 'Not Tested',
    pass:        ko ? '통과'     : 'Pass',
    partial:     ko ? '부분 통과': 'Partial',
    fail:        ko ? '실패(취약)': 'Fail (Vulnerable)',
    in_progress: ko ? '진행 중'  : 'In Progress',
  };

  const header = ko
    ? ['위협ID', '위협명', '공격벡터', 'CAL등급', 'ASIL', '테스트결과', '발견사항/CVE', 'CVSS점수', 'CVSS등급', '테스터', '메모', '보안통제상태', '보안요구사항']
    : ['ThreatID', 'ThreatName', 'AttackVector', 'CAL', 'ASIL', 'TestResult', 'Finding/CVE', 'CVSSScore', 'CVSSGrade', 'Tester', 'Notes', 'ControlStatus', 'SecurityReqs'];

  const rows = [header.join(',')];

  calMatrix.cal_items
    .filter(ci => ci.cal >= 2)   // CAL 2 이상만 대상
    .forEach(ci => {
      const pt    = pentestResults[ci.id] || {};
      const cvss  = parseFloat(pt.cvss) || null;
      const cvssGrade = cvss == null ? '' :
        cvss >= 9 ? 'Critical' :
        cvss >= 7 ? 'High' :
        cvss >= 4 ? 'Medium' : 'Low';

      const threatName = ko ? (ci.threat    || '') : (ci.threatEn    || ci.threat    || '');
      const atkVector  = ko ? (ci.attack_vector || '') : (ci.attack_vectorEn || ci.attack_vector || '');
      const secReqs    = ko
        ? (ci.sec_req  || []).join('; ')
        : (ci.sec_reqEn || []).join('; ');

      rows.push([
        ci.id,
        `"${threatName}"`,
        `"${atkVector}"`,
        ci.cal,
        ci.max_asil || '',
        RESULT_LABEL[pt.result || 'not_tested'],
        `"${pt.finding || ''}"`,
        cvss ?? '',
        cvssGrade,
        `"${pt.tester  || ''}"`,
        `"${pt.notes   || ''}"`,
        ci.impl_status || '',
        `"${secReqs}"`,
      ].join(','));
    });

  return rows.join('\n');
}

// ── mapPentestToTARA ──
/**
 * 침투 테스트 결과를 TARA 위협과 자동으로 매핑하고, 실패 항목의 CAL을 상향 조정한다.
 *
 * CAL 등급이 4 미만인 위협에서 침투 테스트 결과가 'fail'이면
 * CAL을 한 단계 상향(max 4)하여 보안 요구사항 우선순위를 재조정한다.
 * 이는 [D10] ISO/SAE 21434 Cl.10의 펜테스트 연계 기능의 핵심 로직이다.
 *
 * @param {{ cal_items: object[] }} calMatrix  computeCAL_Matrix()의 반환값
 * @param {object} [pentestResults]  { [threat_id]: { result, finding, cvss, ... } }
 * @returns {{ upgraded: Array<{id, threat, cal_orig, cal_new, cvss, finding}>,
 *             summary: { total, pass, fail, partial, untested } }}
 */
// [D10] pentest 결과 → TARA 위협 자동 매핑 (fail 항목 → 보안 요구사항 우선순위 재조정)
function mapPentestToTARA(calMatrix, pentestResults = {}) {
  if (!calMatrix) return { upgraded: [], summary: {} };
  const items  = calMatrix.cal_items || [];
  const upgraded = [];

  items.forEach(ci => {
    const pt = pentestResults[ci.id] || {};
    if (pt.result === 'fail' && ci.cal < 4) {
      // fail 발견 → CAL 한 단계 상향 플래그
      upgraded.push({
        id:       ci.id,
        threat:   ci.threat,
        cal_orig: ci.cal,
        cal_new:  Math.min(ci.cal + 1, 4),
        cvss:     pt.cvss || null,
        finding:  pt.finding || '',
      });
    }
  });

  const threats = items.filter(ci => ci.cal >= 2);
  const summary = {
    total:    threats.length,
    pass:     threats.filter(ci => (pentestResults[ci.id]?.result || 'not_tested') === 'pass').length,
    fail:     threats.filter(ci => (pentestResults[ci.id]?.result || 'not_tested') === 'fail').length,
    partial:  threats.filter(ci => (pentestResults[ci.id]?.result || 'not_tested') === 'partial').length,
    untested: threats.filter(ci => !pentestResults[ci.id]?.result ||
                                    pentestResults[ci.id]?.result === 'not_tested').length,
  };
  return { upgraded, summary };
}

// ── _hazardIdOffset ──
/**
 * hazard ID 문자열을 안정적 해시(0.000~0.099)로 변환한다.
 *
 * [Fix-Critical-1] estimated_coverage의 Math.random() 의존성 제거:
 * 렌더링마다 달라지는 Math.random() 대신 ID 기반 결정론적 해시를 사용하여
 * 동일한 파라미터와 hazard ID에 대해 항상 동일한 오프셋을 반환한다.
 * 이를 통해 보고서의 재현성(reproducibility)을 보장한다.
 *
 * 알고리즘: Polynomial Rolling Hash (multiplier=31)를 uint32로 클램프 후 %100/1000
 *
 * @param {string} id  hazard ID 문자열 (예: 'FM-001')
 * @returns {number}  0.000 ~ 0.099 범위의 결정론적 오프셋
 */
// [Fix-Critical-1] estimated_coverage 결정론적 보조 함수
function _hazardIdOffset(id) {
  let h = 0;
  for (let i = 0; i < (id || '').length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return (h % 100) / 1000; // 0.000 ~ 0.099
}

// ── computeISO26262_Part6 ──
/**
 * ISO 26262 Part 6(소프트웨어 수준 제품 개발)에 따른 SW 안전 요구사항을 도출한다.
 *
 * HARA 결과의 ASIL 등급별로 다음 4가지 SW 요구사항 유형을 결정한다:
 *   coding          - 코딩 표준 및 금지 구문 (MISRA C:2012, 동적 메모리 등)
 *   testing         - 커버리지 목표 및 테스트 방법 (MC/DC, Branch, Back-to-back)
 *   architecture    - SW 아키텍처 요건 (AUTOSAR OS, 파티션 보호)
 *   static_analysis - 정적 분석 방법 (추상 해석, 모델 체킹)
 *
 * 커버리지 목표: ASIL D=100%, ASIL C=100%, ASIL B=85%, ASIL A=80%
 *
 * estimated_coverage는 [Fix-Critical-1] _hazardIdOffset()으로 결정론적으로 계산된다
 * (Math.random() 제거 → 보고서 재현성 보장).
 *
 * @param {object[]} haraResults  analyzeHARA()의 반환값 (asil !== 'QM'인 항목 대상)
 * @returns {{ sw_items, asil_summary, vv_checklist, required_count, recommended_cnt, na_count, total_sw_items }}
 */
function computeISO26262_Part6(haraResults) {
  // ASIL별 SW 요구사항 테이블 (Part 6 Table 1~8 기반)
  const SW_REQ_MAP = {
    D: {
      coding: ['MISRA C:2012 (M)', 'No dynamic memory allocation', 'No recursion', 'Defensive impl. (M)', 'Strongly typed lang. (R)'],
      codingKo: ['MISRA C:2012 필수(M)', '동적 메모리 할당 금지', '재귀 호출 금지', '방어적 구현(M)', '강형 언어 사용(R)'],
      testing: ['MC/DC 100%', 'Branch coverage 100%', 'Statement coverage 100%', 'Back-to-back testing (M)', 'Fault injection test (M)'],
      testingKo: ['MC/DC 100%', '브랜치 커버리지 100%', '구문 커버리지 100%', '백투백 테스트(M)', '결함 주입 테스트(M)'],
      architecture: ['Formal verification (R)', 'SW unit isolation', 'AUTOSAR OS (M)', 'Partition protection (M)'],
      architectureKo: ['정형 검증(R)', 'SW 유닛 격리', 'AUTOSAR OS(M)', '파티션 보호(M)'],
      static_analysis: ['Abstract interpretation', 'Model checking (R)', 'Proof of correctness (R)'],
      static_analysisKo: ['추상 해석', '모델 체킹(R)', '정확성 증명(R)'],
      coverage_target: 100, asil_color: '#ef4444',
    },
    C: {
      coding: ['MISRA C:2012 (R)', 'No dynamic memory alloc. (R)', 'Defensive impl. (R)', 'Coding guidelines (M)'],
      codingKo: ['MISRA C:2012 권장(R)', '동적 메모리 할당 금지(R)', '방어적 구현(R)', '코딩 가이드라인(M)'],
      testing: ['MC/DC 100%', 'Branch coverage 100%', 'Statement coverage 100%', 'Back-to-back test (R)'],
      testingKo: ['MC/DC 100%', '브랜치 커버리지 100%', '구문 커버리지 100%', '백투백 테스트(R)'],
      architecture: ['SW unit isolation (R)', 'AUTOSAR OS (R)', 'Error detection (M)'],
      architectureKo: ['SW 유닛 격리(R)', 'AUTOSAR OS(R)', '오류 감지(M)'],
      static_analysis: ['Control flow analysis', 'Data flow analysis', 'Code metrics check'],
      static_analysisKo: ['제어 흐름 분석', '데이터 흐름 분석', '코드 메트릭 검사'],
      coverage_target: 100, asil_color: '#f97316',
    },
    B: {
      coding: ['MISRA C:2012 (R)', 'Defensive impl. (R)', 'Coding guidelines (M)'],
      codingKo: ['MISRA C:2012 권장(R)', '방어적 구현(R)', '코딩 가이드라인(M)'],
      testing: ['Branch coverage ≥85%', 'Statement coverage 100%', 'Functional testing (M)'],
      testingKo: ['브랜치 커버리지 ≥85%', '구문 커버리지 100%', '기능 테스트(M)'],
      architecture: ['Error detection (M)', 'Defensive measures (R)'],
      architectureKo: ['오류 감지(M)', '방어적 조치(R)'],
      static_analysis: ['Code complexity check', 'Data flow analysis'],
      static_analysisKo: ['코드 복잡도 검사', '데이터 흐름 분석'],
      coverage_target: 85, asil_color: '#f59e0b',
    },
    A: {
      coding: ['Coding guidelines (M)', 'Review (M)'],
      codingKo: ['코딩 가이드라인(M)', '코드 리뷰(M)'],
      testing: ['Statement coverage ≥80%', 'Functional testing (M)'],
      testingKo: ['구문 커버리지 ≥80%', '기능 테스트(M)'],
      architecture: ['Error detection (R)'],
      architectureKo: ['오류 감지(R)'],
      static_analysis: ['Basic static analysis'],
      static_analysisKo: ['기본 정적 분석'],
      coverage_target: 80, asil_color: '#22c55e',
    },
  };

  // 고장모드별 SW 컴포넌트 매핑
  const SW_COMPONENT_MAP = {
    '센서': 'Sensor Signal Processing SW',
    'Sensor': 'Sensor Signal Processing SW',
    '통신': 'Communication Stack SW',
    'Comms': 'Communication Stack SW',
    '모터': 'Motor Control SW (FOC)',
    'Motor': 'Motor Control SW (FOC)',
    'BMS': 'Battery Management SW',
    '전력전자': 'Power Electronics Control SW',
    'Power Elec.': 'Power Electronics Control SW',
    '제어': 'Vehicle Control Unit SW',
    'Control': 'Vehicle Control Unit SW',
    '안전회로': 'Safety Monitor SW',
    'Safety Ckt': 'Safety Monitor SW',
    '열관리': 'Thermal Management SW',
    'Thermal': 'Thermal Management SW',
    '제동': 'Brake Control SW',
    'Braking': 'Brake Control SW',
  };

  // ASIL별 SW 요구사항 집계
  const sw_items = (haraResults || [])
    .filter(h => h.asil && h.asil !== 'QM')
    .map(h => {
      const req = SW_REQ_MAP[h.asil] || SW_REQ_MAP['A'];
      const sw_comp = SW_COMPONENT_MAP[h.category] || SW_COMPONENT_MAP[h.categoryEn] || 'Application SW';
      return {
        hazard_id: h.id, name: h.name, nameEn: h.nameEn || h.name,
        asil: h.asil, asil_color: req.asil_color, sw_component: sw_comp,
        safety_goal: h.safety_goal, safety_goalEn: h.safety_goalEn,
        coding: req.coding, codingKo: req.codingKo,
        testing: req.testing, testingKo: req.testingKo,
        architecture: req.architecture, architectureKo: req.architectureKo,
        static_analysis: req.static_analysis, static_analysisKo: req.static_analysisKo,
        coverage_target: req.coverage_target,
        // [Fix-Critical-1] SW 구현 상태 시뮬레이션 — 결정론적 hazard ID 해시 기반
        // 이전: Math.random() → 렌더링마다 달라져 보고서 재현 불가
        // 수정: _hazardIdOffset(h.id)로 ID 기반 안정적 오프셋 산출
        //       동일 파라미터 + 동일 ID → 항상 동일한 값 반환
        estimated_coverage: +(req.coverage_target * Math.min(0.99, 0.88 + _hazardIdOffset(h.id))).toFixed(1),
      };
    });

  // ASIL별 집계
  const asil_summary = ['D','C','B','A'].map(asil => ({
    asil,
    count: sw_items.filter(s => s.asil === asil).length,
    components: [...new Set(sw_items.filter(s => s.asil === asil).map(s => s.sw_component))],
    coverage_target: SW_REQ_MAP[asil]?.coverage_target || 80,
    color: SW_REQ_MAP[asil]?.asil_color || '#60a5fa',
  }));

  // SW V&V 체크리스트 (Part 6 Cl.9~11)
  const vv_checklist = [
    { id:'VV-01', phase:'Unit Test', req:'Statement coverage 100% (ASIL C/D)', standard:'ISO 26262-6 Cl.9.4.3', status: sw_items.some(s=>['C','D'].includes(s.asil)) ? 'required' : 'recommended' },
    { id:'VV-02', phase:'Unit Test', req:'Branch coverage 100% (ASIL B/C/D)', standard:'ISO 26262-6 Cl.9.4.3', status:'required' },
    { id:'VV-03', phase:'Unit Test', req:'MC/DC 100% (ASIL C/D)', standard:'ISO 26262-6 Cl.9.4.3', status: sw_items.some(s=>['C','D'].includes(s.asil)) ? 'required' : 'na' },
    { id:'VV-04', phase:'Integration Test', req:'Fault injection at SW interface (ASIL D)', standard:'ISO 26262-6 Cl.10.4.3', status: sw_items.some(s=>s.asil==='D') ? 'required' : 'recommended' },
    { id:'VV-05', phase:'Integration Test', req:'Back-to-back test (reference model vs. impl)', standard:'ISO 26262-6 Cl.10.4.3', status:'recommended' },
    { id:'VV-06', phase:'Static Analysis', req:'MISRA C:2012 compliance (ASIL C/D mandatory)', standard:'ISO 26262-6 Cl.8.4.4', status: sw_items.some(s=>['C','D'].includes(s.asil)) ? 'required' : 'recommended' },
    { id:'VV-07', phase:'Static Analysis', req:'Abstract interpretation / formal methods (ASIL D)', standard:'ISO 26262-6 Cl.8.4.4', status: sw_items.some(s=>s.asil==='D') ? 'required' : 'na' },
    { id:'VV-08', phase:'Architecture', req:'SW partition protection (ASIL D)', standard:'ISO 26262-6 Cl.7.4.15', status: sw_items.some(s=>s.asil==='D') ? 'required' : 'na' },
    { id:'VV-09', phase:'Architecture', req:'AUTOSAR OS or equivalent (ASIL C/D)', standard:'ISO 26262-6 Cl.7.4.13', status: sw_items.some(s=>['C','D'].includes(s.asil)) ? 'required' : 'recommended' },
    { id:'VV-10', phase:'Review', req:'SW safety requirements walkthrough (all ASIL)', standard:'ISO 26262-6 Cl.6.4.6', status:'required' },
  ];

  const required_count  = vv_checklist.filter(v => v.status === 'required').length;
  const recommended_cnt = vv_checklist.filter(v => v.status === 'recommended').length;
  const na_count        = vv_checklist.filter(v => v.status === 'na').length;

  return { sw_items, asil_summary, vv_checklist, required_count, recommended_cnt, na_count,
    total_sw_items: sw_items.length };
}

// ── computeSafetyTriangle ──
/**
 * ISO 26262(기능안전) × ISO/SAE 21434(사이버보안) × ISO 21448(SOTIF)의 3중 연계 분석을 수행한다.
 *
 * '안전 삼각형(Safety Triangle)' 개념으로 ASIL D/C 위험원, TARA Critical/High 위협,
 * SOTIF High-Risk 시나리오를 교차 매핑하여 3중 연계(triple_linked)와 2중 연계(dual_linked) 항목을 식별한다.
 *
 * SOTIF 연계는 SOTIF_CATEGORY_MAP 테이블을 통해 HARA 카테고리(센서/모터/제동/열관리/통신) →
 * Known Scenario ID(KS-01~KS-16)로 매핑된다. [Fix-5] 기존 2종에서 5종으로 확장.
 *
 * 종합 커버리지 점수:
 *   coverage_score = (triple_count×100 + dual_count×50) / (total×100) × 100
 *
 * @param {object[]} haraResults   analyzeHARA()의 반환값
 * @param {object}   taraResult    computeTARA_Part4()의 반환값
 * @param {object}   sotifResult   computeSOTIF()의 반환값
 * @returns {{ triangle_links, triple_count, dual_count, iso_gap_count, coverage_score, ... } | null}
 *   입력이 null이면 null 반환.
 */
function computeSafetyTriangle(haraResults, taraResult, sotifResult) {
  if (!haraResults || !taraResult || !sotifResult) return null;

  // ASIL D/C 고장모드 추출
  const high_asil = (haraResults || []).filter(h => ['D','C'].includes(h.asil));

  // TARA Critical/High 손상 시나리오 추출
  const critical_threats = (taraResult.damage_analysis || [])
    .filter(d => ['CRITICAL','HIGH'].includes(d.risk_level));

  // SOTIF High-Risk 시나리오 추출
  const sotif_high = ((sotifResult || {}).known_scenarios || [])
    .filter(s => s.residual_risk === 'HIGH' || s.oedr < 0.90);

  // 3자 연계: HARA fm_link 통해 TARA와 연결, SOTIF은 카테고리 기반 매핑
  const triangle_links = [];
  high_asil.forEach(hara => {
    const tara_link = critical_threats.find(t => t.fm_link === hara.id);
    // [Fix-5] SOTIF 연계 카테고리 매핑 테이블 — 센서/모터 외 제동·열관리·통신 추가
    const SOTIF_CATEGORY_MAP = {
      '센서':  ['KS-03','KS-04','KS-07','KS-08'], 'Sensor':  ['KS-03','KS-04','KS-07','KS-08'],
      '모터':  ['KS-03','KS-04','KS-07','KS-08'], 'Motor':   ['KS-03','KS-04','KS-07','KS-08'],
      '제동':  ['KS-01','KS-02','KS-05'],          'Braking': ['KS-01','KS-02','KS-05'],
      '열관리':['KS-06','KS-09','KS-10'],          'Thermal': ['KS-06','KS-09','KS-10'],
      '통신':  ['KS-02','KS-05','KS-08'],          'Comm':    ['KS-02','KS-05','KS-08'],
    };
    const cat = hara.category || hara.categoryEn || '';
    const allowedKS = SOTIF_CATEGORY_MAP[cat] || [];
    const sotif_link = allowedKS.length > 0
      ? sotif_high.find(s => allowedKS.includes(s.id))
      : null;
    triangle_links.push({
      hara_id: hara.id, hara_name: hara.name, hara_nameEn: hara.nameEn || hara.name,
      asil: hara.asil,
      tara_id: tara_link?.id || null, tara_scenario: tara_link?.scenario || null,
      tara_scenarioEn: tara_link?.scenarioEn || null,
      tara_risk: tara_link?.risk_level || null,
      sotif_id: sotif_link?.id || null, sotif_scenario: sotif_link?.scenario || null,
      sotif_scenarioEn: sotif_link?.scenarioEn || null,
      sotif_risk: sotif_link?.residual_risk || null,
      // 3중 연계 여부
      triple_linked: !!(tara_link && sotif_link),
      dual_linked: !!(tara_link || sotif_link),
    });
  });

  const triple_count = triangle_links.filter(l => l.triple_linked).length;
  const dual_count   = triangle_links.filter(l => l.dual_linked && !l.triple_linked).length;
  const iso_gap_count = triangle_links.filter(l => !l.dual_linked).length;

  // 도메인별 coverage 점수 (0~100)
  const coverage_score = triangle_links.length > 0
    ? Math.round((triple_count * 100 + dual_count * 50) / (triangle_links.length * 100) * 100)
    : 0;

  return { triangle_links, triple_count, dual_count, iso_gap_count,
    coverage_score, total: triangle_links.length,
    high_asil_count: high_asil.length,
    critical_threat_count: critical_threats.length,
    sotif_high_count: sotif_high.length };
}

// ── computeCAL_Matrix ──
/**
 * ISO/SAE 21434 Table 8에 따라 위협별 CAL(Cybersecurity Assurance Level) 등급을 산출한다.
 *
 * CAL은 attack_feasibility(공격 실현 가능성)와 risk_value(영향도)의 조합으로 결정된다:
 *   입력 변환:
 *     risk_value (0-10) → imp_s (0-3): round(rv/10×3)
 *     attack_feasibility → ap_s (1-4): 고/High=4, 중/Medium=3, 저/Low=2, Very Low=1
 *   ASIL 보정: ASIL D 컨텍스트에서 ap_s를 1 상향하여 보수적 CAL 산출
 *
 * [Fix-Critical-2] CAL 결정 SST 단일화:
 *   구 연속 수식((risk_value/10)*4*(feasNum/3)+asilBonus)과 fmea.js CAL_DECISION_TABLE의
 *   결과 불일치 수정. _CAL_TABLE을 fmea.js와 동일한 Table 8 행렬로 통일.
 *
 * CAL 등급별 보안 요구사항 자동 도출:
 *   CAL 4 → 암호화 키 관리(HSM), CAL 3 → MAC + 안전 부트, CAL 2 → 접근 제어
 *
 * @param {{ damage_analysis, threat_analysis, asset_analysis } | null} taraResult  computeTARA_Part4() 반환값
 * @returns {{ cal_items, cal_summary, max_cal, cal4_count, cal3_count } | null}
 *   taraResult가 null이면 null 반환.
 */
function computeCAL_Matrix(taraResult) {
  if (!taraResult) return null;

  // CAL 결정 테이블 (ISO/SAE 21434 Table 8 기반)
  // [Fix-Critical-2] getCAL: fmea.js CAL_DECISION_TABLE 기반으로 통일
  // 이전: 연속 수식((risk_value/10)*4*(feasNum/3)+asilBonus) → fmea.js CAL_MAP과 결과 불일치
  // 수정: risk_value(0-10)·attack_feasibility 를 Table 스케일로 변환 후
  //       fmea.js CAL_DECISION_TABLE과 동일한 테이블 참조 → SST 달성
  //
  // 입력 변환:
  //   risk_value (0-10)      → imp_s (0-3):  Math.round(rv / 10 * 3)
  //   attack_feasibility     → ap_s  (1-5):  고→4, 중→3, 저→2 (High→4, Medium→3, Low→2)
  //   asil_level             → ASIL D/C는 ap_s 상향 보정 (테이블 경계에서 일치성 유지)
  //
  // fmea.js CAL_DECISION_TABLE (ISO/SAE 21434 Table 8) 인라인 참조
  const _CAL_TABLE = [
    [0, 0, 0, 0, 0],
    [0, 1, 1, 2, 2],
    [0, 1, 2, 3, 3],
    [0, 2, 3, 4, 4],
  ];
  function getCAL(risk_value, attack_feasibility, asil_level) {
    // risk_value 0-10 → imp_s 0-3
    const imp_s = Math.min(3, Math.round(risk_value / 10 * 3));
    // [Fix-4] attack_feasibility: 5인자 정량 등급(High/Medium/Low/Very Low) 또는 하위호환 문자열
    const feasMap = { '고': 4, 'High': 4, '중': 3, 'Medium': 3, '저': 2, 'Low': 2, 'Very Low': 1 };
    let ap_s = feasMap[attack_feasibility] || 3;
    // ASIL D/C 컨텍스트: 공격 용이성 한 단계 상향 (안전 관련 자산의 CAL 보수적 산출)
    if (asil_level === 'ASIL D' && ap_s < 5) ap_s = Math.min(5, ap_s + 1);
    const cal = _CAL_TABLE[imp_s][Math.min(4, ap_s - 1)] || 1;
    return Math.max(1, Math.min(4, cal));
  }

  const cal_items = taraResult.threat_analysis.map(th => {
    const related_ds = taraResult.damage_analysis.filter(d =>
      taraResult.asset_analysis.some(a => a.id === d.asset_id && th.asset_ids.includes(a.id))
    );
    const max_asil = related_ds.reduce((best, d) => {
      const rank = { 'ASIL D': 4, 'ASIL C': 3, 'ASIL B': 2, 'ASIL A': 1 };
      return (rank[d.required_asil] || 0) > (rank[best] || 0) ? d.required_asil : best;
    }, 'ASIL A');
    // [Fix-4] feasibility_factors가 있으면 5인자 정량 계산, 없으면 문자열 하위호환
    const feasRating = th.feasibility_factors
      ? computeAttackFeasibility(th.feasibility_factors).rating
      : th.attack_feasibility;
    const cal = getCAL(th.threat_risk, feasRating, max_asil);

    // 보안 요구사항 자동 도출
    const sec_req = [];
    const sec_reqEn = [];
    if (cal >= 4) { sec_req.push('암호화 키 관리 HSM'); sec_reqEn.push('HSM-based crypto key mgmt'); }
    if (cal >= 3) { sec_req.push('메시지 인증(MAC/CMAC)'); sec_reqEn.push('Message authentication (MAC/CMAC)'); }
    if (cal >= 3) { sec_req.push('안전 부트 + 코드 서명'); sec_reqEn.push('Secure boot + code signing'); }
    if (cal >= 2) { sec_req.push('접근 제어 정책'); sec_reqEn.push('Access control policy'); }
    if (cal >= 2) { sec_req.push('이상 감지 모니터링'); sec_reqEn.push('Anomaly detection monitoring'); }
    sec_req.push('로그 기록 + 감사 추적'); sec_reqEn.push('Logging + audit trail');

    // 대응 현황 (시뮬레이션)
    const impl_status = cal >= 4 ? 'planned' : cal >= 3 ? 'in_progress' : 'implemented';

    return {
      ...th, cal, max_asil, sec_req, sec_reqEn, impl_status,
      cal_color: cal === 4 ? '#ef4444' : cal === 3 ? '#f97316' : cal === 2 ? '#f59e0b' : '#22c55e',
    };
  });

  const cal_summary = [4,3,2,1].map(c => ({
    cal: c, count: cal_items.filter(i => i.cal === c).length,
    label: `CAL ${c}`, color: [null,'#22c55e','#f59e0b','#f97316','#ef4444'][c],
  }));

  return { cal_items, cal_summary,
    max_cal: Math.max(...cal_items.map(i => i.cal), 1),
    cal4_count: cal_items.filter(i => i.cal === 4).length,
    cal3_count: cal_items.filter(i => i.cal === 3).length,
  };
}

export {
  computeTARA_Part4,
  deriveSecurityControls,
  deriveCountermeasure,
  generateTARA_Report,
  computeISO26262_Part6,
  computeSafetyTriangle,
  computeCAL_Matrix,
  generatePentestReport,
  mapPentestToTARA,
};